import {
  ConsultantContractType,
  ConsultantBookingContractType,
  ConsultantContractPensionSolution
} from "../enums/ConsultantContract";
import { getConsultantAgeGroup } from "./utils";
import { IConsultantContractStrategy } from "./consultant-contract-strategy";
import { BcFrameworkConfirmationContractStrategy } from "./strategies/bc-framework-confirmation.contract-strategy";
import { BcFrameworkContractStrategy } from "./strategies/bc-framework.contract-strategy";
import { BcIntermittentConfirmationContractStrategy } from "./strategies/bc-intermittent-confirmation.contract-strategy";
import { BcIntermittentContractStrategy } from "./strategies/bc-intermittent.contract-strategy";
import { BookingConfirmationContractStrategy } from "./strategies/booking-confirmation.contract-strategy";
import { BookingOutsourceContractStrategy } from "./strategies/booking-outsource.contract-strategy";
import { ConfirmationContractStrategy } from "./strategies/confirmation.contract-strategy";
import { ConfirmationForEmployeesContractStrategy } from "./strategies/confirmation-for-employees.contract-strategy";
import { ConfirmationForEmployeesNationalContractStrategy } from "./strategies/confirmation-for-employees-national.contract-strategy";
import { ConfirmationNationalContractStrategy } from "./strategies/confirmation-national.contract-strategy";
import { DefaultContractStrategy } from "./strategies/default.contract-strategy";
import { EmploymentContractStrategy } from "./strategies/employment.contract-strategy";
import { EmploymentNationalFullContractStrategy } from "./strategies/employment-national-full.contract-strategy";
import { EmploymentNationalSplitContractStrategy } from "./strategies/employment-national-split.contract-strategy";
import { EmploymentUskContractStrategy } from "./strategies/employment-usk.contract-strategy";
import { FrameworkContractStrategy } from "./strategies/framework.contract-strategy";
import { FrameworkNationalContractStrategy } from "./strategies/framework-national.contract-strategy";
import { IntermittentContractStrategy } from "./strategies/intermittent.contract-strategy";
import { IntermittentNationalFullContractStrategy } from "./strategies/intermittent-national-full.contract-strategy";
import { IntermittentNationalSplitContractStrategy } from "./strategies/intermittent-national-split.contract-strategy";
import { IntermittentUskContractStrategy } from "./strategies/intermittent-usk.contract-strategy";
import { OutsourceContractStrategy } from "./strategies/outsource.contract-strategy";
import { OutsourceNationalContractStrategy } from "./strategies/outsource-national.contract-strategy";
import { SelfInvoicingContractStrategy } from "./strategies/self-invoicing.contract-strategy";

/**
 * Represents the parameters required by the ConsultantContractStrategyFactory class.
 *
 * @interface ConsultantContractStrategyFactoryParams
 */
export interface ConsultantContractStrategyFactoryParams {
  type: ConsultantContractType | ConsultantBookingContractType;
  isConsultantBookingContract?: boolean;
  profession: string;
  consultantBirthDate: string; //format: YYYY-MM-DD
  pensionSolution: ConsultantContractPensionSolution | null;
  periodStart: string | null; //format: YYYY-MM-DD
  periodEnd: string | null; //format: YYYY-MM-DD

  logger?: (message: string, ...params: any) => void;
}

/**
 * A factory class for creating consultant contract strategies based on parameters.
 *
 * @see https://bonliva.atlassian.net/wiki/spaces/CRM/pages/203489282/Consultant+Contract+Configuration
 */
export class ConsultantContractStrategyFactory {
  static create(
    params: ConsultantContractStrategyFactoryParams
  ): IConsultantContractStrategy {
    const {
      type,
      profession,
      periodStart,
      consultantBirthDate,
      periodEnd,
      pensionSolution,
      logger = console.log
    } = params;

    logger(
      `Creating contract strategy for type: ${type}, profession: ${profession}, periodStart: ${periodStart}, periodEnd: ${periodEnd}, pensionSolution: ${pensionSolution}, birthday: ${consultantBirthDate}`
    );

    if (
      type === ConsultantContractType.Outsource &&
      !params.isConsultantBookingContract
    ) {
      logger("Creating OutsourceContractStrategy");
      return new OutsourceContractStrategy();
    }

    if (type === ConsultantContractType.Framework) {
      logger("Creating FrameworkContractStrategy");
      return new FrameworkContractStrategy();
    }

    if (
      type === ConsultantContractType.Confirmation &&
      !params.isConsultantBookingContract
    ) {
      logger("Creating ConfirmationContractStrategy");
      return new ConfirmationContractStrategy();
    }

    if (type === ConsultantContractType.Employment && profession === "USK") {
      logger("Creating EmploymentUskContractStrategy");
      return new EmploymentUskContractStrategy();
    }

    if (type === ConsultantContractType.Employment) {
      logger("Creating EmploymentContractStrategy");
      return new EmploymentContractStrategy();
    }

    if (type === ConsultantContractType.Intermittent && profession === "USK") {
      logger("Creating IntermittentUskContractStrategy");
      return new IntermittentUskContractStrategy();
    }

    if (type === ConsultantContractType.Intermittent) {
      logger("Creating IntermittentContractStrategy");
      return new IntermittentContractStrategy();
    }

    if (type === ConsultantContractType.ConfirmationForEmployees) {
      logger("Creating ConfirmationForEmployeesContractStrategy");
      return new ConfirmationForEmployeesContractStrategy();
    }

    if (type === ConsultantContractType.Outsource_National) {
      logger("Creating OutsourceNationalContractStrategy");
      return new OutsourceNationalContractStrategy();
    }

    if (type === ConsultantContractType.Framework_National) {
      logger("Creating FrameworkNationalContractStrategy");
      return new FrameworkNationalContractStrategy();
    }

    if (type === ConsultantContractType.Confirmation_National) {
      logger("Creating ConfirmationNationalContractStrategy");
      return new ConfirmationNationalContractStrategy();
    }

    if (type === ConsultantContractType.Employment_National) {
      if (!periodStart || !periodEnd) {
        logger("Creating DefaultContractStrategy");
        return new DefaultContractStrategy();
      }

      const ageGroup = getConsultantAgeGroup(
        params.consultantBirthDate,
        periodStart,
        periodEnd
      );

      logger("Age group: ", { ...ageGroup });

      if (ageGroup.is25To64) {
        logger("Creating EmploymentNationalFullContractStrategy");
        return new EmploymentNationalFullContractStrategy();
      }

      if (
        (ageGroup.becomes25 || ageGroup.becomes65) &&
        pensionSolution === ConsultantContractPensionSolution.Full
      ) {
        logger("Creating EmploymentNationalFullContractStrategy");
        return new EmploymentNationalFullContractStrategy();
      }

      if (
        (ageGroup.becomes25 || ageGroup.becomes65) &&
        pensionSolution === ConsultantContractPensionSolution.Split
      ) {
        logger("Creating EmploymentNationalSplitContractStrategy");
        return new EmploymentNationalSplitContractStrategy();
      }

      if (ageGroup.above64 || ageGroup.below25) {
        logger("Creating EmploymentNationalSplitContractStrategy");
        return new EmploymentNationalSplitContractStrategy();
      }

      return new DefaultContractStrategy();
    }

    if (type === ConsultantContractType.Intermittent_National) {
      if (!periodStart || !periodEnd) {
        logger("Creating DefaultContractStrategy");
        return new DefaultContractStrategy();
      }

      const ageGroup = getConsultantAgeGroup(
        params.consultantBirthDate,
        periodStart,
        periodEnd
      );

      if (ageGroup.is25To64) {
        logger("Creating IntermittentNationalFullContractStrategy");
        return new IntermittentNationalFullContractStrategy();
      }

      if (
        (ageGroup.becomes25 || ageGroup.becomes65) &&
        pensionSolution === ConsultantContractPensionSolution.Full
      ) {
        logger("Creating IntermittentNationalFullContractStrategy");
        return new IntermittentNationalFullContractStrategy();
      }

      if (
        (ageGroup.becomes25 || ageGroup.becomes65) &&
        pensionSolution === ConsultantContractPensionSolution.Split
      ) {
        logger("Creating IntermittentNationalSplitContractStrategy");
        return new IntermittentNationalSplitContractStrategy();
      }

      if (ageGroup.above64 || ageGroup.below25) {
        logger("Creating IntermittentNationalSplitContractStrategy");
        return new IntermittentNationalSplitContractStrategy();
      }

      return new DefaultContractStrategy();
    }

    if (type === ConsultantContractType.ConfirmationForEmployees_National) {
      logger("Creating ConfirmationForEmployeesNationalContractStrategy");
      return new ConfirmationForEmployeesNationalContractStrategy();
    }

    if (type === ConsultantContractType.BC_Framework) {
      logger("Creating BcFrameworkContractStrategy");
      return new BcFrameworkContractStrategy();
    }

    if (type === ConsultantContractType.BC_Framework_Confirmation) {
      logger("Creating BcFrameworkConfirmationContractStrategy");
      return new BcFrameworkConfirmationContractStrategy();
    }

    if (type === ConsultantContractType.BC_Intermittent) {
      logger("Creating BcIntermittentContractStrategy");
      return new BcIntermittentContractStrategy();
    }

    if (type === ConsultantContractType.BC_Intermittent_Confirmation) {
      logger("Creating BcIntermittentConfirmationContractStrategy");
      return new BcIntermittentConfirmationContractStrategy();
    }

    if (type === ConsultantContractType.SelfInvoicing) {
      logger("Creating SelfInvoicingContractStrategy");
      return new SelfInvoicingContractStrategy();
    }

    if (
      type === ConsultantBookingContractType.Confirmation &&
      params.isConsultantBookingContract
    ) {
      logger("Creating BookingConfirmationContractStrategy");
      return new BookingConfirmationContractStrategy();
    }

    if (
      type === ConsultantBookingContractType.Outsource &&
      params.isConsultantBookingContract
    ) {
      logger("Creating BookingOutsourceContractStrategy");
      return new BookingOutsourceContractStrategy();
    }

    throw new Error("Unknown contract type");
  }
}
